import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Truck, CreditCard } from "lucide-react";
import { toast } from "sonner";

function RegistrySection({ title, icon: Icon, endpoint, slug, placeholder, description, capitalize }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => api.get(endpoint).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async () => {
    const n = name.trim();
    if (!n) return;
    setSaving(true);
    try {
      await api.post(endpoint, { name: n });
      toast.success("Voce aggiunta");
      setName("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Voce eliminata");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Card className="rounded-lg border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-heading text-lg">
          <Icon className="h-5 w-5 text-[#002FA7]" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-sm text-slate-500">{description}</p>
        <div className="mb-4 flex gap-2">
          <Input
            data-testid={`${slug}-name-input`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder={placeholder}
            className="max-w-xs"
          />
          <Button onClick={add} disabled={saving || !name.trim()} data-testid={`add-${slug}-btn`} className="rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]">
            <Plus className="mr-2 h-4 w-4" /> Aggiungi
          </Button>
        </div>

        {items.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">Nessuna voce. Aggiungine una.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid={`${slug}-list`}>
            {items.map((t) => (
              <span
                key={t.id}
                data-testid={`${slug}-${t.id}`}
                className={`flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 py-1.5 pl-4 pr-2 text-sm text-slate-700 ${capitalize ? "capitalize" : ""}`}
              >
                {t.name}
                <button
                  onClick={() => setDeleteTarget(t)}
                  data-testid={`delete-${slug}-${t.id}`}
                  className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-rose-100 hover:text-rose-600"
                  aria-label={`Elimina ${t.name}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid={`delete-${slug}-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Eliminare "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              La voce non sarà più selezionabile. Gli elementi esistenti che la usano non vengono modificati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => { await remove(deleteTarget.id); setDeleteTarget(null); }}
              data-testid={`confirm-delete-${slug}-btn`}
              className="bg-rose-600 text-white hover:bg-rose-700"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default function Anagrafiche() {
  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Anagrafiche</h1>
        <p className="mt-1 text-sm text-slate-500">Gestisci le voci personalizzabili dell'applicazione.</p>
      </div>

      <div className="space-y-6">
        <RegistrySection
          title="Tipologie mezzi"
          icon={Truck}
          endpoint="/vehicle-types"
          slug="vehicle-type"
          placeholder="es. Autocarro"
          description="Aggiungi o rimuovi le tipologie selezionabili quando crei un mezzo."
          capitalize
        />
        <RegistrySection
          title="Gestori carte carburante"
          icon={CreditCard}
          endpoint="/card-providers"
          slug="card-provider"
          placeholder="es. Enilive"
          description="Aggiungi o rimuovi i gestori selezionabili quando crei una carta carburante."
        />
      </div>
    </div>
  );
}
